import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Heart, ArrowRight, Settings } from "lucide-react";
import dashboardScreenshot from "@/assets/dashboard-screenshot.png";
import eventsScreenshot from "@/assets/events-screenshot.png";
import membersScreenshot from "@/assets/members-screenshot.png";

export default function TrialExpired() {
  const { isPtaAdmin } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
            <Heart className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Thank You for Trying PTA Hub!</h1>
          <p className="text-xl text-muted-foreground">
            We hope you saw the value and potential in using our platform to manage your PTA
          </p>
        </div>

        {/* Screenshots Section */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <img 
                src={dashboardScreenshot} 
                alt="Dashboard Overview" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold">Dashboard Insights</h3>
                <p className="text-sm text-muted-foreground">Real-time overview of your PTA</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <img 
                src={eventsScreenshot} 
                alt="Events Management" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold">Event Management</h3>
                <p className="text-sm text-muted-foreground">Organize and track events easily</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <img 
                src={membersScreenshot} 
                alt="Member Management" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold">Member Management</h3>
                <p className="text-sm text-muted-foreground">Keep your community connected</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Conditional Content Based on Role */}
        {isPtaAdmin ? (
          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-background">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-4">Next Steps for Your PTA</h2>
              <p className="text-muted-foreground mb-6">
                Your 28-day trial has ended, but all your data is safe and waiting for you. 
                Choose a plan to continue using PTA Hub and unlock the full potential of your PTA management.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Review Our Plans</h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      Choose the plan that best fits your PTA's needs
                    </p>
                    <Button asChild variant="outline" size="sm">
                      <Link to="/pricing">
                        View Pricing Plans <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Set Up Payment</h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      Add your payment details in the PTA Admin dashboard
                    </p>
                    <Button asChild size="sm">
                      <Link to="/pta-admin">
                        Go to PTA Admin <Settings className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-background/50 rounded-lg border">
                <p className="text-sm text-muted-foreground">
                  <strong>Note:</strong> Your account will be automatically removed in 60 days if no plan is selected. 
                  All your data will be permanently deleted at that time.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-primary/20">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Love What You've Seen?</h2>
              <p className="text-muted-foreground mb-6">
                Let your PTA admin know how much you liked the platform! 
                They can decide if your PTA will upgrade to a paid plan to continue using all the amazing features.
              </p>
              
              <div className="max-w-md mx-auto p-6 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-sm font-medium mb-2">Speak to your PTA Admin about:</p>
                <ul className="text-sm text-muted-foreground space-y-2 text-left">
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Which features you found most useful</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>How it made PTA activities easier</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>The value it could bring to your community</span>
                  </li>
                </ul>
              </div>

              <Button asChild className="mt-6">
                <Link to="/">
                  Return to Home <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
